import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Lead } from "../lib/api";
import { InquirySlideOver } from "../components/InquirySlideOver";

type TFunc = (key: string, opts?: Record<string, unknown>) => string;

function timeAgo(iso: string, t: TFunc): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t("common.just_now");
  if (mins < 60) return t("common.mins_ago", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("common.hours_ago", { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 30) return t("common.days_ago", { count: days });
  return new Date(iso).toLocaleDateString("nl-NL", { dateStyle: "medium" });
}

export function LeadsPage() {
  const { t } = useTranslation();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [convertLead, setConvertLead] = useState<Lead | null>(null);

  // Manual lead creation form state
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formFeedback, setFormFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const loadLeads = () => {
    setLoading(true);
    api
      .listLeads()
      .then(setLeads)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadLeads();
  }, []);

  // Focus the name field when the form opens
  useEffect(() => {
    if (showForm) {
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [showForm]);

  function openForm() {
    setFormName("");
    setFormEmail("");
    setFormMessage("");
    setFormFeedback(null);
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setFormFeedback(null);
  }

  async function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormSubmitting(true);
    setFormFeedback(null);
    try {
      await api.createLead({ name: formName, email: formEmail, message: formMessage });
      setFormFeedback({ ok: true, msg: t("leads.form.success") });
      setShowForm(false);
      loadLeads();
    } catch (err) {
      setFormFeedback({ ok: false, msg: err instanceof Error ? err.message : t("leads.form.error") });
    } finally {
      setFormSubmitting(false);
    }
  }

  return (
    <div>
      <InquirySlideOver
        open={!!convertLead}
        initialName={convertLead?.Name ?? ""}
        initialContact={convertLead?.Email ?? ""}
        leadId={convertLead?.ID}
        onClose={() => setConvertLead(null)}
        onCreated={() => {
          // Re-fetch so HandledAt is reflected without a manual refresh.
          api.listLeads().then(setLeads).catch(() => {});
          setConvertLead(null);
        }}
      />

      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <h1 style={{ margin: 0 }}>{t("leads.title")}</h1>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <span style={{ color: "var(--muted)", fontSize: "0.875rem" }}>{t("leads.description")}</span>
          <button className="btn-primary" style={{ fontSize: "0.875rem" }} onClick={openForm}>
            + {t("leads.form.add_button")}
          </button>
        </div>
      </div>

      {formFeedback && (
        <div className={formFeedback.ok ? "alert alert-success" : "alert alert-error"} style={{ marginBottom: "1rem" }}>
          {formFeedback.msg}
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom: "1.25rem" }}>
          <form onSubmit={handleFormSubmit}>
            <div style={{ display: "grid", gap: "0.75rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                  <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                    {t("leads.form.name")} <span style={{ color: "var(--accent)" }}>*</span>
                  </span>
                  <input
                    ref={nameRef}
                    type="text"
                    className="input"
                    required
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder={t("leads.form.name_placeholder")}
                  />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                  <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                    {t("leads.form.email")} <span style={{ color: "var(--accent)" }}>*</span>
                  </span>
                  <input
                    type="email"
                    className="input"
                    required
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    placeholder={t("leads.form.email_placeholder")}
                  />
                </label>
              </div>
              <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>{t("leads.form.message")}</span>
                <textarea
                  className="input"
                  rows={3}
                  value={formMessage}
                  onChange={(e) => setFormMessage(e.target.value)}
                  placeholder={t("leads.form.message_placeholder")}
                  style={{ resize: "vertical" }}
                />
              </label>
              <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                <button type="button" className="btn-ghost" onClick={cancelForm} disabled={formSubmitting}>
                  {t("common.cancel")}
                </button>
                <button type="submit" className="btn-primary" disabled={formSubmitting}>
                  {formSubmitting ? t("common.saving") : t("leads.form.submit")}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <p style={{ color: "var(--muted)" }}>Loading…</p>}

      {!loading && leads.length === 0 && (
        <div className="card">
          <p style={{ margin: 0, color: "var(--muted)" }}>
            {t("leads.empty")}
          </p>
        </div>
      )}

      <div style={{ display: "grid", gap: "0.75rem" }}>
        {leads.map((l) => {
          const isConverted = !!l.HandledAt;
          return (
            <div
              key={l.ID}
              className="card"
              style={{ cursor: "pointer", opacity: isConverted ? 0.6 : 1 }}
              onClick={() => setExpanded(expanded === l.ID ? null : l.ID)}
            >
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", flexWrap: "wrap" }}>
                  <strong style={{ fontSize: "1.05rem" }}>{l.Name}</strong>
                  {l.Email && (
                    <a
                      href={`mailto:${l.Email}`}
                      style={{ color: "var(--accent)", fontSize: "0.875rem" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {l.Email}
                    </a>
                  )}
                  {isConverted && (
                    <span style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#10b981",
                      background: "color-mix(in srgb, #10b981 12%, transparent)",
                      padding: "0.1rem 0.5rem",
                      borderRadius: 999,
                    }}>
                      {t("leads.converted")}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  {!isConverted && (
                    <button
                      className="btn-ghost"
                      style={{ fontSize: "0.8rem", padding: "0.25rem 0.6rem" }}
                      onClick={(e) => { e.stopPropagation(); setConvertLead(l); }}
                    >
                      {t("leads.add_inquiry")}
                    </button>
                  )}
                  <span style={{ color: "var(--muted)", fontSize: "0.8rem", whiteSpace: "nowrap" }}>{timeAgo(l.CreatedAt, t)}</span>
                </div>
              </div>
              {expanded === l.ID && (
                <p style={{ marginTop: "0.75rem", marginBottom: 0, whiteSpace: "pre-wrap", color: "var(--text)" }}>{l.Message}</p>
              )}
              {expanded !== l.ID && l.Message && (
                <p style={{ marginTop: "0.4rem", marginBottom: 0, color: "var(--muted)", fontSize: "0.875rem", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  {l.Message}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
